import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { useEffect, useId } from 'react';
import { type FieldValues, type Path, type PathValue, type RegisterOptions, useFormContext, useFormState } from 'react-hook-form';
import { formatValue } from '../../../../utils/mask-utils';
import type { LabelTooltipProps } from '../../../label';
import TextInput, { type TextInputProps } from '../../../text-input';
import FormWrapper from '../../internals/form-wrapper';
export type FormInputProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  label?: string | React.ReactNode;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  errorPosition?: 'absolute' | 'relative';
  orientation?: 'vertical' | 'horizontal' | 'horizontal-justify-between';
  addon?: React.ReactNode;
  placeholderIcon?: React.ReactNode;
  tooltip?: LabelTooltipProps;
  containerClassName?: string;
  hideError?: boolean;
} & Omit<TextInputProps, 'value' | 'ref' | 'label' | 'hasError'>;
export default function FormInput(t0) {
  const $ = _c(50);
  const {
    name,
    label,
    rules,
    hint,
    id: baseId,
    size: t1,
    errorPosition: t2,
    orientation: t3,
    addon,
    placeholderIcon,
    onChange,
    onBlur,
    tooltip,
    containerClassName,
    hideError: t4,
    ...inputProps
  } = t0;
  const size = t1 === undefined ? "default" : t1;
  const errorPosition = t2 === undefined ? "relative" : t2;
  const orientation = t3 === undefined ? "vertical" : t3;
  const hideError = t4 === undefined ? false : t4;
  const {
    register,
    getValues,
    setValue,
    control,
    getFieldState
  } = useFormContext();
  const formState = useFormState({
    control,
    name,
    disabled: inputProps.disabled
  });
  let t5;
  if ($[0] !== formState || $[1] !== getFieldState || $[2] !== name) {
    t5 = getFieldState(name, formState);
    $[0] = formState;
    $[1] = getFieldState;
    $[2] = name;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const {
    error
  } = t5;
  const contextId = useId();
  const id = baseId || contextId;
  const hasMask = !!inputProps.mask;
  const T0 = TextInput;
  let t6;
  if ($[4] !== onChange || $[5] !== rules) {
    t6 = e => {
      onChange?.(e);
      rules?.onChange?.(e);
    };
    $[4] = onChange;
    $[5] = rules;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== onBlur || $[8] !== rules) {
    t7 = e_0 => {
      onBlur?.(e_0);
      rules?.onBlur?.(e_0);
    };
    $[7] = onBlur;
    $[8] = rules;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== name || $[11] !== register || $[12] !== rules || $[13] !== t6 || $[14] !== t7) {
    t8 = register(name, {
      ...rules,
      onChange: t6,
      onBlur: t7
    });
    $[10] = name;
    $[11] = register;
    $[12] = rules;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = formState.disabled || inputProps.disabled;
  const t10 = !!error;
  const t11 = "mask";
  const t12 = cx({
    "rounded-bl-none! rounded-tl-none!": addon,
    "pl-10": placeholderIcon
  }, inputProps.className);
  let t13;
  if ($[16] !== T0 || $[17] !== containerClassName || $[18] !== id || $[19] !== inputProps || $[20] !== size || $[21] !== t10 || $[22] !== t12 || $[23] !== t8 || $[24] !== t9) {
    t13 = <T0 {...inputProps} {...t8} disabled={t9} hasError={t10} id={id} size={size} data-dd-privacy={t11} containerClassName={containerClassName} className={t12} />;
    $[16] = T0;
    $[17] = containerClassName;
    $[18] = id;
    $[19] = inputProps;
    $[20] = size;
    $[21] = t10;
    $[22] = t12;
    $[23] = t8;
    $[24] = t9;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const textInput = t13;
  let t14;
  if ($[26] !== getValues || $[27] !== hasMask || $[28] !== inputProps || $[29] !== name || $[30] !== setValue) {
    t14 = () => {
      if (!hasMask) {
        return;
      }
      const value = getValues(name);
      if (!value) {
        return;
      }
      const formatted = formatValue(value, inputProps.mask);
      if (formatted !== value) {
        setValue(name, formatted as PathValue<TFieldValues, Path<TFieldValues>>, {
          shouldDirty: false
        });
      }
    };
    $[26] = getValues;
    $[27] = hasMask;
    $[28] = inputProps;
    $[29] = name;
    $[30] = setValue;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== hasMask) {
    t15 = [hasMask];
    $[32] = hasMask;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  useEffect(t14, t15);
  const t16 = error?.message;
  const t17 = formState.disabled || inputProps.disabled;
  let t18;
  if ($[34] !== placeholderIcon || $[35] !== textInput) {
    t18 = placeholderIcon ? <div className="relative"><div className="absolute left-2 top-1/2 -translate-y-1/2 z-10 flex items-center pr-4 h-full" role="img">{placeholderIcon}</div>{textInput}</div> : textInput;
    $[34] = placeholderIcon;
    $[35] = textInput;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== addon || $[38] !== errorPosition || $[39] !== hideError || $[40] !== hint || $[41] !== id || $[42] !== label || $[43] !== orientation || $[44] !== size || $[45] !== t16 || $[46] !== t17 || $[47] !== t18 || $[48] !== tooltip) {
    t19 = <FormWrapper id={id} error={t16} label={label} errorPosition={errorPosition} size={size} hint={hint} addon={addon} orientation={orientation} fullWidth={true} disabled={t17} tooltip={tooltip} hideError={hideError}>{t18}</FormWrapper>;
    $[37] = addon;
    $[38] = errorPosition;
    $[39] = hideError;
    $[40] = hint;
    $[41] = id;
    $[42] = label;
    $[43] = orientation;
    $[44] = size;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
    $[48] = tooltip;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  return t19;
}